import { DisplayMeasurementProfileDetail } from "@fairfleet/geotab";
import { GridApiPro, GridColDef, GridInitialState, useGridApiRef } from "@mui/x-data-grid-pro";
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import React, {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  RefObject,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { useOptionalViewContext } from "./ViewProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { usePresetContext } from "@/components/PresetProvider";
import { PAGE_CONFIGS, PageType } from "@/constants/PageType";
import { presetUserId } from "@/hooks/addins/presetUserId";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { usePresetColumns } from "@/hooks/presets/usePresetColumns";
import useDisplayMeasurementProfileDetail from "@/hooks/useDisplayMeasurementProfileDetail";
import { useUserDisplayProfile } from "@/hooks/users/useUserDisplayProfile";
import { useViewColumns } from "@/hooks/views/useViewColumns";
import { AlertRule } from "@/models/AlertRule";
import ColorInterval from "@/models/ColorInterval";
import { AlertDisplayMode, DataGridSettingsAddInData } from "@/models/DataGridSettingsAddInData";

interface DataGridSettingsContextProps extends DataGridSettingsAddInData {
  setAlertRules: Dispatch<SetStateAction<AlertRule[]>>;
  setIncludeDismissedWarnings: Dispatch<SetStateAction<boolean>>;
  setDeviceCountLimit: Dispatch<SetStateAction<number>>;
  setLoadAddresses: Dispatch<SetStateAction<boolean>>;
  setPrecisions: Dispatch<SetStateAction<Record<string, number>>>;
  setHideOldData: Dispatch<SetStateAction<boolean>>;
  setColorIntervals: Dispatch<SetStateAction<Record<string, ColorInterval[]>>>;
  setColumnNames: Dispatch<SetStateAction<Record<string, string>>>;
  setShowArchivedVehicles: Dispatch<SetStateAction<boolean>>;
  setChargeRateOnlyWhenCharging: Dispatch<SetStateAction<boolean>>;
  highlightChargeInterrupts: boolean;
  setHighlightChargeInterrupts: Dispatch<SetStateAction<boolean>>;
  alertDisplayMode: AlertDisplayMode;
  setAlertDisplayMode: Dispatch<SetStateAction<AlertDisplayMode>>;
  alertAutoDismiss: boolean;
  setAlertAutoDismiss: Dispatch<SetStateAction<boolean>>;
  diagnosticUnits: DisplayMeasurementProfileDetail[];
  pageType: PageType;
  saveGridState: (id?: string) => Promise<boolean>;
  apiRef: RefObject<GridApiPro>;
  columns: GridColDef[];
  loading: boolean;
  loadSettings: (details: DataGridSettingsAddInData) => void;
  gridState: GridInitialStatePro | null;
  templatePrefix: string;
}

const DataGridSettingsContext = createContext<
  Omit<DataGridSettingsContextProps, "id" | "dataGridState" | "groups"> | undefined
>(undefined);

export const DataGridSettingsProvider: React.FC<{
  children: React.ReactNode;
  pageType: PageType;
}> = ({ children, pageType }) => {
  const addInId = PAGE_CONFIGS[pageType].addInId;
  const templatePrefix = "template-";
  const [loading, setLoading] = useState(true);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [precisions, setPrecisions] = useState<Record<string, number>>({});
  const [columnNames, setColumnNames] = useState<Record<string, string>>({});
  const [colorIntervals, setColorIntervals] = useState<Record<string, ColorInterval[]>>({});
  const [includeDismissedWarnings, setIncludeDismissedWarnings] = useState(false);
  const [deviceCountLimit, setDeviceCountLimit] = useState(1000);
  const [loadAddresses, setLoadAddresses] = useState(true);
  const [hideOldData, setHideOldData] = useState(false);

  const [highlightChargeInterrupts, setHighlightChargeInterrupts] = useState(true);
  const [showArchivedVehicles, setShowArchivedVehicles] = useState(false);
  const [chargeRateOnlyWhenCharging, setChargeRateOnlyWhenCharging] = useState(false);
  const [alertDisplayMode, setAlertDisplayMode] = useState<AlertDisplayMode>("subtle");
  const [alertAutoDismiss, setAlertAutoDismiss] = useState(true);

  const viewCtx = useOptionalViewContext();
  const { geotabClient, me } = useGeotabClient();
  const { selectedPreset: preset, isReady: presetReady } = usePresetContext(pageType);

  const updateAddIn = useUpdateAddIn<DataGridSettingsAddInData>(addInId, "id");
  const { data: settingsAddIns = [] } = useAddinData<DataGridSettingsAddInData>(addInId);
  const storeId = presetUserId(me?.id ?? "", preset?.id ?? "");

  const apiRef = useGridApiRef();

  const { data: userDisplayProfile } = useUserDisplayProfile(me?.id);
  const { data: diagnosticUnits = [] } = useDisplayMeasurementProfileDetail(
    userDisplayProfile?.profileId ?? undefined,
  );

  const [gridState, setGridState] = useState<GridInitialStatePro | null>(null);

  const updateSettings = useCallback((settings: Partial<DataGridSettingsContextProps>) => {
    if (!settings) return;
    if (settings.alertRules) setAlertRules(settings.alertRules);
    if (settings.precisions) setPrecisions(settings.precisions);
    if (settings.colorIntervals) setColorIntervals(settings.colorIntervals);
    if (settings.columnNames) setColumnNames(settings.columnNames);
    if (settings.includeDismissedWarnings !== undefined)
      setIncludeDismissedWarnings(settings.includeDismissedWarnings);
    if (settings.deviceCountLimit !== undefined) setDeviceCountLimit(settings.deviceCountLimit);
    if (settings.loadAddresses !== undefined) setLoadAddresses(settings.loadAddresses);
    if (settings.hideOldData !== undefined) setHideOldData(settings.hideOldData);
    if (settings.chargeRateOnlyWhenCharging !== undefined)
      setChargeRateOnlyWhenCharging(settings.chargeRateOnlyWhenCharging);
    if (settings.showArchivedVehicles !== undefined)
      setShowArchivedVehicles(settings.showArchivedVehicles);
    if (settings.alertDisplayMode !== undefined) setAlertDisplayMode(settings.alertDisplayMode);
    if (settings.alertAutoDismiss !== undefined) setAlertAutoDismiss(settings.alertAutoDismiss);
  }, []);

  const loadSettings = useCallback(
    async (details: DataGridSettingsAddInData) => {
      const state: GridInitialState = JSON.parse(details?.dataGridState ?? "{}");
      updateSettings({
        alertRules: details.alertRules ?? [],
        precisions: details.precisions,
        colorIntervals: details.colorIntervals,
        columnNames: details.columnNames,
        includeDismissedWarnings: details.includeDismissedWarnings,
        deviceCountLimit: details.deviceCountLimit,
        loadAddresses: details.loadAddresses,
        hideOldData: details.hideOldData,
        chargeRateOnlyWhenCharging: details.chargeRateOnlyWhenCharging ?? false,
        showArchivedVehicles: details.showArchivedVehicles ?? false,
        alertDisplayMode: details.alertDisplayMode ?? "subtle",
        alertAutoDismiss: details.alertAutoDismiss ?? true,
      });
      setGridState(applyDefaultParkingVisibility(state));
    },
    [updateSettings],
  );

  useLayoutEffect(() => {
    const fetchInitialState = async () => {
      if (viewCtx?.selectedView) {
        const v = viewCtx.selectedView;
        const o = viewCtx.override?.settings ?? {};
        console.warn("[DataGridSettings] view-mode apply", {
          viewId: v.id,
          viewDeviceCountLimit: v.settings.deviceCountLimit,
          overrideDeviceCountLimit: o.deviceCountLimit,
          overrideFull: viewCtx.override,
        });
        updateSettings({
          alertRules: o.alertRules ?? v.settings.alertRules ?? [],
          precisions: o.precisions ?? v.settings.precisions,
          colorIntervals: o.colorIntervals ?? v.settings.colorIntervals,
          columnNames: o.columnNames ?? v.settings.columnNames,
          includeDismissedWarnings:
            o.includeDismissedWarnings ?? v.settings.includeDismissedWarnings,
          deviceCountLimit: o.deviceCountLimit ?? v.settings.deviceCountLimit,
          loadAddresses: o.loadAddresses ?? v.settings.loadAddresses,
          hideOldData: o.hideOldData ?? v.settings.hideOldData,
          chargeRateOnlyWhenCharging:
            o.chargeRateOnlyWhenCharging ?? v.settings.chargeRateOnlyWhenCharging,
          showArchivedVehicles: o.showArchivedVehicles ?? v.settings.showArchivedVehicles,
          alertDisplayMode: o.alertDisplayMode ?? v.settings.alertDisplayMode,
          alertAutoDismiss: o.alertAutoDismiss ?? v.settings.alertAutoDismiss,
        });
        setGridState(applyDefaultParkingVisibility(viewCtx.mergedGridState));
        setLoading(false);
        return;
      }

      // Legacy preset mode
      if (!me?.id || (!preset?.id && !presetReady)) return;
      // A user who sees no preset (none exist, or none shared with their groups) gets the
      // defaults; waiting for a preset here left the grid in its loading state forever.
      const details = preset?.id
        ? settingsAddIns.find(settings => settings.id === storeId)
        : undefined;
      if (details) {
        loadSettings(details);
      } else {
        setAlertRules([]);
        setGridState(
          applyDefaultParkingVisibility({
            sorting: {
              sortModel: [
                {
                  field: "ignitionStatus",
                  sort: "desc",
                },
              ],
            },
            pinnedColumns: {
              left: [],
              right: [],
            },
          }),
        );
      }
      setLoading(false);
    };
    fetchInitialState();
  }, [
    apiRef,
    geotabClient,
    preset?.id,
    presetReady,
    me?.id,
    storeId,
    addInId,
    loadSettings,
    settingsAddIns,
    viewCtx?.selectedView,
    viewCtx?.mergedGridState,
    viewCtx?.override,
    updateSettings,
  ]);

  const saveGridState = useCallback(
    async (id = storeId) => {
      if (!apiRef?.current?.exportState) return false;
      const currentState = apiRef.current.exportState();
      if (currentState?.pagination) {
        currentState.pagination.rowCount = undefined;
        currentState.pagination.paginationModel = undefined;
      }

      if (viewCtx?.selectedView) return true;

      if (!addInId || !me?.id || !preset?.id) return false;
      try {
        // updateAddIn resolves false when the write fails, so pass its result through.
        return await updateAddIn.mutateAsync({
          id,
          dataGridState: JSON.stringify(currentState),
          alertRules,
          precisions,
          colorIntervals,
          columnNames,
          hideOldData,
          includeDismissedWarnings,
          deviceCountLimit,
          loadAddresses,
          chargeRateOnlyWhenCharging,
          showArchivedVehicles,
          alertDisplayMode,
          alertAutoDismiss,
        } as DataGridSettingsAddInData);
      } catch (error) {
        console.error("Failed to save grid state", error);
        return false;
      }
    },
    [
      addInId,
      alertRules,
      apiRef,
      chargeRateOnlyWhenCharging,
      colorIntervals,
      columnNames,
      deviceCountLimit,
      hideOldData,
      includeDismissedWarnings,
      loadAddresses,
      me?.id,
      precisions,
      preset?.id,
      showArchivedVehicles,
      storeId,
      updateAddIn,
      alertDisplayMode,
      alertAutoDismiss,
      viewCtx?.selectedView,
    ],
  );

  const { columns: presetColumns } = usePresetColumns(pageType);
  const { columns: viewColumns } = useViewColumns();
  const columns = viewCtx?.selectedView ? viewColumns : presetColumns;

  const contextValue = useMemo(() => {
    return {
      alertRules,
      setAlertRules,
      precisions,
      setPrecisions,
      colorIntervals,
      setColorIntervals,
      columnNames,
      setColumnNames,
      includeDismissedWarnings,
      setIncludeDismissedWarnings,
      deviceCountLimit,
      setDeviceCountLimit,
      loadAddresses,
      setLoadAddresses,
      hideOldData,
      setHideOldData,
      showArchivedVehicles,
      setShowArchivedVehicles,
      chargeRateOnlyWhenCharging,
      setChargeRateOnlyWhenCharging,
      diagnosticUnits,
      pageType,
      saveGridState,
      apiRef,
      columns,
      loading,
      gridState,
      loadSettings,
      templatePrefix,
      highlightChargeInterrupts,
      setHighlightChargeInterrupts,
      alertDisplayMode,
      setAlertDisplayMode,
      alertAutoDismiss,
      setAlertAutoDismiss,
    };
  }, [
    alertRules,
    precisions,
    colorIntervals,
    columnNames,
    includeDismissedWarnings,
    deviceCountLimit,
    loadAddresses,
    hideOldData,
    showArchivedVehicles,
    chargeRateOnlyWhenCharging,
    diagnosticUnits,
    pageType,
    saveGridState,
    apiRef,
    columns,
    loadSettings,
    loading,
    gridState,
    highlightChargeInterrupts,
    setHighlightChargeInterrupts,
    alertDisplayMode,
    alertAutoDismiss,
  ]);

  return (
    <DataGridSettingsContext.Provider value={contextValue}>
      {children}
    </DataGridSettingsContext.Provider>
  );
};

export const useDataGridSettings = () => {
  const context = useContext(DataGridSettingsContext);
  if (!context) {
    throw new Error("useDataGridSettings must be used within a DataGridSettingsProvider");
  }
  return context;
};

function applyDefaultParkingVisibility(gridState?: GridInitialStatePro): GridInitialStatePro {
  if (!gridState) {
    return {
      columns: {
        columnVisibilityModel: {
          parking: false,
        },
      },
    };
  }

  const newState: GridInitialStatePro = structuredClone(gridState);

  if (!newState.columns) {
    newState.columns = { columnVisibilityModel: { parking: false } };
    return newState;
  }

  if (!newState.columns.columnVisibilityModel) {
    newState.columns.columnVisibilityModel = { parking: false };
    return newState;
  }

  if (newState.columns.columnVisibilityModel.parking !== undefined) {
    return newState;
  }

  newState.columns.columnVisibilityModel.parking = false;

  return newState;
}
